import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useEdition } from '../../contexts/EditionContext';
import CircledPlusIcon from '../icons/CircledPlus';
import PlayIcon from '../icons/Play';
import WatchIcon from '../icons/Watch';
import Date from '../partials/Date';
import FilmTitle from '../partials/FilmTitle';
import IconLink from '../partials/IconLink';
import Link from '../partials/Link';
import Poster from '../partials/Poster';
import Tag from '../partials/Tag';

import styles from '../../../styles/cards/online-film.module.scss';

const propTypes = {
    item: AppPropTypes.listItem,
    className: PropTypes.string,
};

const defaultProps = {
    item: null,
    className: null,
};

const OnlineFilmCard = ({ item, className }) => {
    const currentEdition = useEdition();
    const { handle = null } = currentEdition || {};
    const {
        playUrl = null,
        availableAt = null,
        expiresAt = null,
        expired = false,
        film = null,
    } = item;
    const { url: filmUrl = null, edition = null } = film || {};
    const { handle: year = null } = edition || {};

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.disabled]: expired,
                    [className]: className !== null,
                },
            ])}
        >
            {expired ? (
                <Link className={styles.posterLink} href={filmUrl}>
                    <Tag
                        className={styles.tag}
                        label={year}
                        color={year === handle ? 'highlight' : 'dark'}
                    />
                    <Poster className={styles.poster} item={film} isDark />
                </Link>
            ) : (
                <Link className={styles.posterLink} href={playUrl} external>
                    <PlayIcon className={styles.playIcon} />
                    <Poster className={styles.poster} item={film} isDark />
                </Link>
            )}
            <div className={styles.content}>
                <FilmTitle
                    linkClassName={styles.link}
                    titleClassName={styles.title}
                    metaItemClassName={styles.meta}
                    item={film}
                    url={filmUrl}
                    isLink
                />
                {availableAt !== null ? (
                    <div className={styles.info}>
                        <div className={styles.label}>
                            <FormattedMessage
                                defaultMessage="Available from:"
                                description="Date prefix"
                            />
                        </div>
                        <Date date={availableAt} className={styles.heading} />
                    </div>
                ) : null}
                {expiresAt !== null ? (
                    <div className={styles.info}>
                        <div className={styles.label}>
                            <FormattedMessage
                                defaultMessage="Available until:"
                                description="Date prefix"
                            />
                        </div>
                        <Date date={expiresAt} className={styles.heading} />
                    </div>
                ) : null}
                {!expired ? (
                    <div className={styles.info}>
                        <IconLink
                            href={playUrl}
                            className={classNames(styles.button, styles.watchOnlineButton)}
                            icon={<WatchIcon />}
                            iconPosition="left"
                            isDark
                            external
                        >
                            <FormattedMessage
                                defaultMessage="Watch online"
                                description="Button label"
                            />
                        </IconLink>
                    </div>
                ) : null}
                <div className={styles.info}>
                    <IconLink
                        className={classNames(styles.button, styles.viewButton)}
                        href={filmUrl}
                        icon={<CircledPlusIcon />}
                        iconPosition="left"
                        isDark
                    >
                        <FormattedMessage
                            defaultMessage="View film page"
                            description="Button label"
                        />
                    </IconLink>
                </div>
            </div>
        </div>
    );
};

OnlineFilmCard.propTypes = propTypes;
OnlineFilmCard.defaultProps = defaultProps;

export default OnlineFilmCard;
